import { useState, useEffect } from 'react';
import { TestimonialsModule } from 'features/Home/LoggedOutModules/TestimonialsModule';
import { TestimonialCard } from 'components/TestimonialCard';
import { webAssetUrl } from 'lib/utils/imageUtils';
import { GondolaHead } from 'components/GondolaHead/GondolaHead';
import { useApi } from 'lib/contexts/ApplicationState';
import { ESServiceMeta } from 'server/api/types';

const About = () => {
  const API = useApi();
  const [jared, setJared] = useState<any>();

  useEffect(() => {
    async function fetchUsers() {
      const users = await API.ignoreErrors(
        API.getTestimonials({
          byUserId: [4],
        }), { data: [], meta: {} as ESServiceMeta },
      );
      setJared(users?.data?.[0]);
    }

    if (!jared) {
      fetchUsers();
    }
  }, []);

  const quotes = [
    `We believe in supporting the incredible creative community - Gondola offers a much needed
    utility for their daily life.`,
    `Creators deserve credit for their work; not just influencers, but every single creative
    that has even the smallest part in bringing a piece of content to life. By giving them a
    voice, we open up opportunities, data and analytics, and much more.`,
    `When you credit your creators, you are saying that their work is valued. And when creatives
    feel valued, they make great content. By building a standardized global crediting system,
    we are excited to be the ones changing the standard for the future of social content.
    Empowering the whole industry is our goal, from opportunities to analytics to community,
    and we're loving the ride.`,
  ];

  return (
    <>
      <GondolaHead
        title="About"
        description="Gondola is the first system of credits for social media, providing opportunities,
          analytics, and portfolios for the whole creative industry."
        url="https://gondola.cc/about"
      />
      <div className="bg-alpineTeal">
        <div className="max-w-screen-xl m-auto text-center text-white p-6 py-12">
          <img src={webAssetUrl('logo-white1.png')} alt="Gondola" className="md:max-w-lg m-auto mb-4" />
          <h1 className="text-center text-2xl font-bold mb-2">
            Connecting Creators to Their Content
          </h1>
          <div className="text-xl">
            Provide the first system of credits for social media, and in doing so,
            provide
            {' '}
            <div className="text-xl text-gold font-bold">opportunities, analytics, and portfolios</div>
            {' '}
            for the whole creative industry
          </div>
        </div>
        <div className="bg-white md:py-10">
          <div className="max-w-screen-xl p-6 mx-auto">
            <h2 className="text-3xl font-bold mb-4">Our Story</h2>
            <p>
              What started as an internal agency database
              became a tool for the whole social media ecosystem.
              <br />
              <br />
              Back in 2018, the Denver-based production company
              {' '}
              <a href="/freshtapemedia" className="text-orange underline">Fresh Tape Media</a>
              {' '}
              was looking for a way to
              collect and showcase social analytics to all their clients without infringing
              on usage rights or just keeping an Excel spreadsheet like everyone else.
              “Fresh Tape Admin”
              (sexy name, we know) was born, the first iteration of Gondola to be used as an
              internal tool for
              {' '}
              <a href="/jarzod" className="text-orange underline">Jared Kleinstein</a>
              {' '}
              and his team. It was used this
              way for 2.5 years.
              <br />
              <br />
              When the tool to tag creators from the team was added, a lightbulb moment of
              “wait, did we just build a system to give creators credit for their work on other
              people&apos;s accounts/channels? Like IMDB for social media?” set off a wild series
              of events. What was supposed to be an internal tool became a tool made available for
              every creative around the globe.
            </p>
          </div>
        </div>
        <div className="md:py-10">
          <div className="max-w-screen-xl bg-gold rounded-lg px-6 py-10 mx-auto flex flex-wrap items-center">
            <div className="w-full sm:w-1/2 mb-4">
              <span className="text-3xl font-semibold">Gondola</span>
              {' '}
              is named after a ski Gondola because yes, you can make it to the top alone, but
              the ride is always better with a great group of people. Oh, and we&apos;re based in
              Colorado, so it was inevitable that we&apos;d have a ski-related name.
              <br />
              <br />
              {' '}
              Gondola&apos;s
              beta officially launched in February 2022, and the iOS app dropped in November 2022.
              <br />
              <br />
              {' '}
              We&apos;re happy to have you along for the ride!
            </div>
            <div className="w-full sm:w-1/2">
              <img src={webAssetUrl('here-for-the-ride.png')} alt="here for the ride" className="mx-auto" />
            </div>
          </div>
        </div>
        <div className="bg-white md:py-10">
          <div className="max-w-screen-xl p-6 mx-auto">
            <h2 className="text-3xl font-bold mb-4 text-center">Our Team</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div>
                <img src={webAssetUrl('about/Jared.jpg')} alt="Jared Kleinstein" />
                <div className="font-bold text-lg mt-4">Jared Kleinstein</div>
                <div>CEO & Founder</div>
              </div>
              <div>
                <img src={webAssetUrl('about/Brian.jpg')} alt="Brian Bosché" />
                <div className="font-bold text-lg mt-4">Brian Bosché</div>
                <div>VP of GTM</div>
              </div>
              <div>
                <img src={webAssetUrl('about/Alana.jpg')} alt="Alana Moritz" />
                <div className="font-bold text-lg mt-4">Alana Moritz</div>
                <div>Creative Director</div>
              </div>
              <div>
                <img src={webAssetUrl('about/Jordan.jpg')} alt="Jordan Anderson" />
                <div className="font-bold text-lg mt-4">Jordan Anderson</div>
                <div>Engineer</div>
              </div>
              <div>
                <img src={webAssetUrl('about/Sebastian.jpg')} alt="Sebastian Spiegel" />
                <div className="font-bold text-lg mt-4">Sebastian Spiegel</div>
                <div>Engineer</div>
              </div>
            </div>
          </div>
        </div>
        <div className="md:py-10">
          <div className="max-w-screen-xl bg-sky rounded-lg px-6 py-10 mx-auto flex flex-wrap justify-between">
            <div className="w-full sm:w-1/3 max-h-96 overflow-hidden mb-4">
              <img className="h-full mx-auto" src={webAssetUrl('about/MIT-SSAC-winner-2.jpg')} alt="MIT SSAC winner 2023" />
            </div>
            <div className="w-full sm:w-1/2 rounded-lg m-auto">
              <h2 className="text-3xl font-bold mb-2">Award</h2>
              We joined the MIT&nbsp;
              <a
                className="font-bold underline"
                href="https://twitter.com/SloanSportsConf"
                target="_blank"
                rel="noreferrer"
              >
                @SloanSportsConf
              </a>
              &nbsp;in Boston, and we walked away as
              the 2023 Startup and Competition Trade Show first-place winners!
            </div>
          </div>
        </div>
        <div className="max-w-screen-xl mx-auto p-6">
          <h2 className="text-3xl text-white font-semibold mb-4">Testimonials</h2>
          {jared && (
            <TestimonialCard key={jared.id} user={jared} text={quotes[2]} showLinks />
          )}
          <TestimonialsModule />
        </div>
      </div>
    </>
  );
};

export default About;
